import {
  ChangeEvent,
  ClipboardEvent,
  KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from 'react'
import cx from 'classnames'
import { Rule } from 'workflow/interface'

import { stripForbiddenCharacters } from 'common/helpers/stripForbiddenCharacters'
import { replaceNewLineEmojiAsString } from 'common/variantsUtils'

import {
  countNumberOfBreakLines,
  replaceBreakLineCharsWithPhraseeBreakSymbol,
} from './helpers'

type Props = {
  rules?: Rule[]
  value?: string
  className?: string
  maxRows?: number
  rows?: number
  autoSize?: boolean | { minRows?: number; maxRows?: number }
  disabled?: boolean
  placeholder?: string
  id?: string
  name?: string
  maxLength?: number
  onBlur?: React.FocusEventHandler<HTMLTextAreaElement>
  onChange: (value: string, originalValue: string) => void
  onLineBreaksWarning?: () => void
  'data-cy'?: string
  'data-testid'?: string
}

const ControlInput = ({
  className,
  onChange,
  onLineBreaksWarning,
  maxRows,
  rows,
  autoSize,
  value,
  rules,
  disabled,
  placeholder,
  id,
  name,
  maxLength,
  onBlur,
  'data-cy': dataCy,
  'data-testid': dataTestId,
}: Props) => {
  const [count, setCount] = useState(value?.length || 0)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const autoSizeConfig = autoSize
    ? autoSize
    : !rows
      ? { minRows: 1.334, maxRows: 5 }
      : undefined

  const minRows =
    typeof autoSizeConfig === 'object' ? autoSizeConfig.minRows : undefined
  const maxRowsAuto =
    typeof autoSizeConfig === 'object' ? autoSizeConfig.maxRows : undefined

  useEffect(() => {
    const el = textareaRef.current
    if (!el || rows) {
      return
    }

    const style = getComputedStyle(el)
    const lineHeight = parseFloat(style.lineHeight) || 20
    const borderH =
      (parseFloat(style.borderTopWidth) || 0) +
      (parseFloat(style.borderBottomWidth) || 0)
    const paddingH =
      (parseFloat(style.paddingTop) || 0) +
      (parseFloat(style.paddingBottom) || 0)

    // Count actual line breaks — 〽 is the Phrasee break symbol, \n covers server-sourced values
    const breakCount = (value?.match(/\n|〽/g) || []).length

    const minH = (minRows ?? 1) * lineHeight + paddingH + borderH
    const maxH =
      maxRowsAuto && maxRowsAuto > 0
        ? maxRowsAuto * lineHeight + paddingH + borderH
        : undefined

    let newH = (breakCount + 1) * lineHeight + paddingH + borderH
    newH = Math.max(newH, minH)
    if (maxH !== undefined) {
      newH = Math.min(newH, maxH)
    }

    el.style.height = `${newH}px`
  }, [value, minRows, maxRowsAuto, rows])

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    const val = (event.target as HTMLTextAreaElement).value
    const numberOfBreakLines = countNumberOfBreakLines(val)

    if (
      maxRows !== undefined &&
      event.key === 'Enter' &&
      numberOfBreakLines >= maxRows
    ) {
      event.preventDefault()
    }
  }

  const handleOnPaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    const val = event.clipboardData?.getData('Text')
    const numberOfBreakLines = countNumberOfBreakLines(val)

    if (
      maxRows !== undefined &&
      numberOfBreakLines > maxRows &&
      onLineBreaksWarning
    ) {
      onLineBreaksWarning()
    }
  }

  const handleOnChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const val = (event.target as HTMLTextAreaElement).value
    const strippedValue = replaceBreakLineCharsWithPhraseeBreakSymbol(
      stripForbiddenCharacters(val)
    )

    if (maxCharacterCount && strippedValue.length > maxCharacterCount) {
      return
    }
    setCount(strippedValue.length)
    onChange(strippedValue, val)
  }

  const maxCharacterCount = rules?.find((rule) => rule.max)?.max

  return (
    <div className={cx({ 'flex flex-col relative -top-5': maxCharacterCount })}>
      {maxCharacterCount && (
        <div className="self-end mb-2 text-coolGray-400">
          {count} / {maxCharacterCount}
        </div>
      )}
      <textarea
        ref={textareaRef}
        data-cy={dataCy}
        data-testid={dataTestId}
        className={cx(
          'updated-component w-full border border-gold-400 rounded-sm px-3 py-2 text-sm text-base-700 placeholder-gold-700 bg-gold-40 outline-none shadow-none resize-none',
          'focus:border-maroon-400',
          'disabled:border-coolGray-300 disabled:cursor-not-allowed',
          { 'hover:border-maroon-200': !disabled },
          className
        )}
        onPaste={handleOnPaste}
        onKeyDown={handleKeyDown}
        onChange={handleOnChange}
        value={replaceNewLineEmojiAsString(value ?? '')}
        rows={rows ?? (autoSizeConfig ? 1 : undefined)}
        disabled={disabled}
        placeholder={placeholder}
        id={id}
        name={name}
        maxLength={maxLength}
        onBlur={onBlur}
        style={autoSizeConfig && !rows ? { overflow: 'hidden' } : undefined}
      />
    </div>
  )
}

export default ControlInput
